import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { ValidateHandle } from './ValidateHandle';
import { DropDown, Label, SelectItem } from '@zaiusinc/hera';
import Notification from '../Notification';
import { ContentGraphConfig } from '../../../../types/DataSyncItem';
import { cgStatuses, cgDeleteModes } from '../../../../types/ContentGraph';
import { localeLabel } from '../../../../lib/localeCatalog';
import { CGLanguageSelectDialog } from './CGLanguageSelectDialog';
import styles from './DataSyncFormSection.module.sass';
import DataSyncCgObjectsStore from '../../../../stores/object/DataSyncCgObjectsStore';
import { useObserver } from 'mobx-react-lite';
import { showToasts } from '../../../../lib/toaster';
import { getApiErrorMessage } from '../../../../lib/ZipApiError';
import { Checkbox } from '@blueprintjs/core';
import { DataSyncObjectDefinitionService } from '../../../../services/DataSync/DataSyncObjectDefinitionService';

interface ContentGraphConfigSectionProps {
  disabled?: boolean;
  onChange: (value: ContentGraphConfig) => void;
  value?: ContentGraphConfig;
  instanceId?: string;
  dataSyncCgObjectStore: DataSyncCgObjectsStore | null;
}

const ContentGraphConfigSection = forwardRef<ValidateHandle, ContentGraphConfigSectionProps>(
  (props, ref) => {

    const {disabled, value, onChange, instanceId, dataSyncCgObjectStore} = props;

    const cgConfig: ContentGraphConfig = {
      status: value?.status || 'DRAFT',
      locale: value?.locale || '',
      cms_integration_enabled: value?.cms_integration_enabled || false,
      base_contract: value?.base_contract || '',
      cms_sync_status: value?.cms_sync_status || undefined,
      cms_sync_error: value?.cms_sync_error || undefined,
      delete_config: value?.delete_config || undefined,
    };

    const [activeLanguageItem, setActiveLanguageItem] = React.useState<SelectItem>();
    const [errors, setErrors] = React.useState<{ [key: string]: boolean }>({
      locale: false,
      status: false,
    });
    const [showNewLanguageDialog, setShowNewLanguageDialog] = React.useState<boolean >(false);
    const [languageDropdownOpen, setLanguageDropdownOpen] = React.useState<boolean>(false);

    const [disableCmsIntegration] = React.useState<boolean>(() => {
      if (value?.cms_sync_status === 'SYNC_FAILED') {
        return false;
      }
      return value?.cms_integration_enabled || false;
    });

    const getCmsStatusMessage = (status: string | undefined): string | null => {
      if (!status) return null;

      switch (status) {
        case 'SYNC_PENDING':
          return 'Content type creation in CMS in progress. It can take up to few minutes. Check again in a moment.';
        case 'SYNC_FAILED':
          return `Content type creation in CMS failed${cgConfig.cms_sync_error ? ` with error '${cgConfig.cms_sync_error}'` : ''}. Try again by saving the sync.`;
        default:
          return null;
      }
    };

    const getCmsStatusClass = (status: string | undefined): string => {
      if (!status) return styles.cmsStatusWarning;

      switch (status) {
        case 'SYNC_FAILED':
          return styles.cmsStatusError;
        case 'SYNC_PENDING':
          return styles.cmsStatusWarning;
        default:
          return '';
      }
    };

    useEffect(() => {
      return () => {
        setActiveLanguageItem(undefined);
      };
    }, [instanceId]);

    useImperativeHandle(ref, () => ({
      validate: () => {
        setErrors({
          locale: !cgConfig.locale,
          status: !cgConfig.status,
        });
        return !!(cgConfig.locale && cgConfig.status);
      },
    }));

    const getSourceMetadata = () => {
      return dataSyncCgObjectStore?.getSourceMetadata();
    };

    const getExistingLanguages = (): SelectItem[] => {
      const metadata = getSourceMetadata();
      return metadata?.locales
        ?.map((existingLocale) => ({text: localeLabel(existingLocale), value: existingLocale}))
        || [];
    };

    const onSaveNewLanguage = async (item: SelectItem) => {
      if (!dataSyncCgObjectStore) {
        return;
      }

      try {
        await dataSyncCgObjectStore.updateSourceMetadata([item.value]);
        setActiveLanguageItem(item);
        onChange({...cgConfig, locale: item.value});
        setErrors({...errors, locale: false});
        setShowNewLanguageDialog(false);
        setLanguageDropdownOpen(false);
        showToasts([{intent: 'success', message: 'Language configuration saved successfully.'}]);
      } catch (error: unknown) {
        const message = await getApiErrorMessage(error, 'Unable to configure new language');
        showToasts([{intent: 'danger', message}]);
      }
    };

    const renderAddNewLanguageOption = () => (
      <div key="cg-add-new-language" className="bp5-menu-item" onClick={() => setShowNewLanguageDialog(true)}>
        <span className={styles.createNewText}>+ Add New</span>
      </div>
    );

    const shouldShowCmsIntegrationOption = () => {
      if (!dataSyncCgObjectStore) {
        return false;
      }

      const objectDefinition = dataSyncCgObjectStore.getTransientDestinationObject();

      return dataSyncCgObjectStore.getBaseTypeDefinitions().length > 0
        && dataSyncCgObjectStore.getProductInstance()?.associated_cms_instance?.id
        && DataSyncObjectDefinitionService.hasCGBaseContractFields(objectDefinition?.fields || []);
    };

    return useObserver(() => (
      <>
        {shouldShowCmsIntegrationOption() &&
          <Label label="" disabled={disabled}>
            <Checkbox
              disabled={disabled || disableCmsIntegration}
              checked={cgConfig.cms_integration_enabled}
              label="Make available as CMS Content"
              onChange={() => {
                const newConfig = {...cgConfig, cms_integration_enabled: !cgConfig.cms_integration_enabled};
                onChange(newConfig);
              }}
            />
            {cgConfig.cms_integration_enabled && getCmsStatusMessage(cgConfig.cms_sync_status) && (
              <div className={`${styles.cmsStatusMessage} ${getCmsStatusClass(cgConfig.cms_sync_status)}`}>
                {getCmsStatusMessage(cgConfig.cms_sync_status)}
              </div>
            )}
          </Label>
        }

        <Label label="Status" disabled={disabled}>
          <DropDown
            disabled={disabled}
            initialContent="Select Status"
            items={cgStatuses}
            onItemSelect={(item: SelectItem) => {
              const newData = {...cgConfig, status: item.value};
              onChange(newData);
              setErrors({...errors, status: false});
            }}
            value={cgConfig.status}
            width={390}/>
          <Notification
            show={errors.status} type="error"
            message="Please select a status from Optimizely Graph content type"
          />
        </Label>

        <Label label="Language" disabled={disabled}>
          <DropDown
            activeItem={activeLanguageItem}
            className="languageDropDown"
            bottomSection={disabled ? undefined : renderAddNewLanguageOption()}
            filterable
            loading={dataSyncCgObjectStore?.loadingSourceMetadata}
            disabled={disabled || !getSourceMetadata()}
            initialContent="Select Language"
            items={getExistingLanguages()}
            popoverProps={{
              isOpen: languageDropdownOpen,
              onInteraction: (nextOpen: boolean) => setLanguageDropdownOpen(nextOpen),
            }}
            onItemSelect={(item: SelectItem) => {
              const newData = {...cgConfig, locale: item.value};
              onChange(newData);
              setErrors({...errors, locale: false});
              setLanguageDropdownOpen(false);
            }}
            value={cgConfig.locale}
            width={390}/>
          <Notification
            show={errors.locale} type="error"
            message="Please select a language for Optimizely Graph content type"
          />
        </Label>

        <Label label="Delete Mode" disabled={disabled}>
          <DropDown
            disabled={disabled}
            initialContent="Select Delete Mode"
            items={cgDeleteModes}
            onItemSelect={(item: SelectItem) => {
              const newConfig = {...cgConfig};
              if (item.value === '') {
                delete newConfig.delete_config;
              } else {
                newConfig.delete_config = { mode: item.value };
              }
              onChange(newConfig);
            }}
            value={cgConfig.delete_config?.mode || ''}
            width={390}
          />
        </Label>
        {showNewLanguageDialog && dataSyncCgObjectStore && <CGLanguageSelectDialog
          dataSyncCgObjectStore={dataSyncCgObjectStore}
          existingLanguages={getExistingLanguages()}
          isOpen={showNewLanguageDialog}
          onSave={onSaveNewLanguage}
          onClose={() => setShowNewLanguageDialog(false)}
        />}
      </>
    ));
  });

export default ContentGraphConfigSection;
